import type { RealBetEvent } from "@/features/common/socket/types";
import { CandlestickChart } from "@/shared/ui/TradingChart/charts/CandelstickChart";
import { LineChart } from "@/shared/ui/TradingChart/charts/LineChart";
import { motion } from "framer-motion";
import { FC, useEffect, useMemo, useRef, useState } from "react";

type Direction = "call" | "put";
type ChartType = "line" | "candlestick" | "heikin-ashi" | "bars";

export interface OnboardingChartProps {
  phase: "idle" | "resolving";
  direction: Direction;
  amount: number;
  durationSeconds: number;
  chartType: ChartType;
  style?: React.CSSProperties;
}

const POINT_COUNT = 60;
const WINDOW_SIZE = 40;
const BASE_PRICE = 65000;
const TICK_MS = 1000;

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function buildIdleLineSeries(nowMs: number) {
  const rand = seededRandom(42);
  const points = [];
  let price = BASE_PRICE;
  for (let i = 0; i < POINT_COUNT; i++) {
    price += (rand() - 0.5) * BASE_PRICE * 0.0015;
    points.push({ time: i, price, timestamp: nowMs - (POINT_COUNT - 1 - i) * 1000 });
  }
  return points;
}

function buildIdleCandles(nowMs: number) {
  const rand = seededRandom(42);
  const candles = [];
  let price = BASE_PRICE;
  for (let i = 0; i < POINT_COUNT; i++) {
    const open = price;
    const drift = (rand() - 0.5) * BASE_PRICE * 0.0018;
    const close = open + drift;
    const wickRoom = Math.abs(drift) * 0.6 + rand() * BASE_PRICE * 0.0006;
    const high = Math.max(open, close) + wickRoom * rand();
    const low = Math.min(open, close) - wickRoom * rand();
    candles.push({ time: i, open, high, low, close, timestamp: nowMs - (POINT_COUNT - 1 - i) * 1000 });
    price = close;
  }
  return candles;
}

export const OnboardingChart: FC<OnboardingChartProps> = ({
  phase,
  direction,
  amount,
  durationSeconds,
  chartType,
  style,
}) => {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const baseTimeRef = useRef(0);
  const flagTimeRef = useRef(0);
  const drawRef = useRef({ drawChart: () => {}, drawOverlay: () => {} });
  const smoothedLinePriceRef = useRef(0);
  const smoothedLineTimeRef = useRef(0);

  const idleLineSeries = useMemo(() => {
    const now = Date.now();
    baseTimeRef.current = now - (POINT_COUNT - 1) * 1000;
    return buildIdleLineSeries(now);
  }, []);
  const idleCandles = useMemo(() => {
    const now = Date.now();
    return buildIdleCandles(now);
  }, []);

  const [lineData, setLineData] = useState(idleLineSeries);
  const [candleData, setCandleData] = useState(idleCandles);
  const [currentViewportTime, setCurrentViewportTime] = useState(POINT_COUNT - 1);
  const [entryBet, setEntryBet] = useState<RealBetEvent | null>(null);

  useEffect(() => {
    smoothedLinePriceRef.current = 0;
    smoothedLineTimeRef.current = 0;
    if (phase !== "resolving") {
      setLineData(idleLineSeries);
      setCandleData(idleCandles);
      setCurrentViewportTime(POINT_COUNT - 1);
      setEntryBet(null);
      return;
    }

    const resolveDurationMs = durationSeconds * 1000;
    const rand = seededRandom(direction === "call" ? 7 : 13);
    const sign = direction === "call" ? 1 : -1;
    const startedAt = Date.now();
    let timer: number | undefined;

    const reanchor = <T extends { timestamp: number }>(series: T[]): T[] => {
      const last = series[series.length - 1]!;
      const shiftMs = startedAt - last.timestamp;
      return series.map((p) => ({ ...p, timestamp: p.timestamp + shiftMs }));
    };
    baseTimeRef.current += startedAt - idleLineSeries[idleLineSeries.length - 1]!.timestamp;
    const anchoredIdleLineSeries = reanchor(idleLineSeries);
    const anchoredIdleCandles = reanchor(idleCandles);

    if (chartType === "line") {
      const entryPoint = anchoredIdleLineSeries[anchoredIdleLineSeries.length - 1]!;
      const entryTime = entryPoint.time;
      const entryPrice = entryPoint.price;
      flagTimeRef.current = entryTime + durationSeconds;
      setEntryBet({
        betId: "onboarding-demo-bet",
        updateType: "created",
        status: "open",
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        betType: direction,
        amount,
        entryPrice,
        targetTime: new Date(Date.now() + resolveDurationMs).toISOString(),
      });

      const targetOffset = entryPrice * 0.0022 * sign;
      let series = anchoredIdleLineSeries;
      let walkPrice = entryPrice;
      let noiseVelocity = 0;

      series = [...series, { time: entryTime + 0.001, price: walkPrice, timestamp: startedAt }];
      setLineData(series);
      setCurrentViewportTime(entryTime + 0.001);

      const tick = () => {
        const t = Math.min(1, (Date.now() - startedAt) / resolveDurationMs);
        const commit = t * t * (3 - 2 * t);
        const noiseTaper = 1 - commit * 0.75;
        noiseVelocity = noiseVelocity * 0.6 + (rand() - 0.5) * entryPrice * 0.0009 * noiseTaper;
        const trendTarget = entryPrice + targetOffset * commit;
        walkPrice = walkPrice + noiseVelocity + (trendTarget - walkPrice) * (0.08 + commit * 0.22);
        const nextTime = entryTime + t * durationSeconds;
        series = [...series.slice(-(POINT_COUNT - 1)), { time: nextTime, price: walkPrice, timestamp: Date.now() }];
        setLineData(series);
        setCurrentViewportTime(nextTime);
        if (t >= 1) {
          smoothedLinePriceRef.current = walkPrice;
          smoothedLineTimeRef.current = nextTime;
        } else {
          timer = window.setTimeout(tick, TICK_MS);
        }
      };
      timer = window.setTimeout(tick, TICK_MS);
    } else {
      const lastIdle = anchoredIdleCandles[anchoredIdleCandles.length - 1]!;
      const entryTime = lastIdle.time + 1;
      const entryPrice = lastIdle.close;
      flagTimeRef.current = entryTime + 0.4;
      setEntryBet({
        betId: "onboarding-demo-bet",
        updateType: "created",
        status: "open",
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        betType: direction,
        amount,
        entryPrice,
        targetTime: new Date(Date.now() + resolveDurationMs).toISOString(),
      });

      const targetOffset = entryPrice * 0.0022 * sign;
      let walkPrice = entryPrice;
      let high = entryPrice;
      let low = entryPrice;
      let noiseVelocity = 0;
      setCurrentViewportTime(entryTime);
      setCandleData([...anchoredIdleCandles, { time: entryTime, open: entryPrice, high, low, close: walkPrice, timestamp: startedAt }]);

      const tick = () => {
        const t = Math.min(1, (Date.now() - startedAt) / resolveDurationMs);
        const commit = t * t * (3 - 2 * t);
        const noiseTaper = 1 - commit * 0.75;
        noiseVelocity = noiseVelocity * 0.6 + (rand() - 0.5) * entryPrice * 0.0011 * noiseTaper;
        const trendTarget = entryPrice + targetOffset * commit;
        walkPrice = walkPrice + noiseVelocity + (trendTarget - walkPrice) * (0.08 + commit * 0.22);
        high = Math.max(high, walkPrice);
        low = Math.min(low, walkPrice);
        const liveCandle = { time: entryTime, open: entryPrice, high, low, close: walkPrice, timestamp: Date.now() };
        setCandleData([...anchoredIdleCandles, liveCandle]);
        if (t < 1) timer = window.setTimeout(tick, TICK_MS);
      };
      timer = window.setTimeout(tick, TICK_MS);
    }

    return () => window.clearTimeout(timer);
  }, [phase, direction, amount, durationSeconds, chartType, idleLineSeries, idleCandles]);

  const chartFns =
    chartType === "line"
      ? LineChart({
          data: lineData,
          currentViewportTime,
          windowSize: WINDOW_SIZE,
          isMobile: false,
          scrollOffset: 0,
          baseTimeRef,
          flagTimeRef,
          canvasRef,
          overlayCanvasRef,
          crosshairPosition: null,
          hoveredButton: null,
          activeBets: entryBet ? [entryBet] : [],
          chartRefreshKey: 0,
          selectedTimePeriodSeconds: durationSeconds,
          smoothedLinePriceRef,
          smoothedLineTimeRef,
          verticalBias: 0.35,
        })
      : CandlestickChart({
          data: candleData,
          currentViewportTime,
          windowSize: WINDOW_SIZE,
          isMobile: false,
          scrollOffset: 0,
          baseTimeRef,
          flagTimeRef,
          canvasRef,
          overlayCanvasRef,
          crosshairPosition: null,
          hoveredButton: null,
          activeBets: entryBet ? [entryBet] : [],
          chartRefreshKey: 0,
          selectedTimePeriodSeconds: durationSeconds,
          renderMode: chartType,
          verticalBias: 0.35,
        });
  drawRef.current.drawChart = chartFns.drawChart;
  drawRef.current.drawOverlay = chartFns.drawOverlay;

  const lerpTargetRef = useRef({ price: 0, time: 0 });
  lerpTargetRef.current = { price: lineData[lineData.length - 1]?.price ?? 0, time: currentViewportTime };

  useEffect(() => {
    let raf: number;
    const loop = () => {
      if (chartType === "line") {
        const { price: priceTarget, time: timeTarget } = lerpTargetRef.current;
        if (priceTarget) {
          const price = smoothedLinePriceRef.current;
          smoothedLinePriceRef.current =
            price === 0 || Math.abs(price - priceTarget) / priceTarget > 0.05
              ? priceTarget
              : price + (priceTarget - price) * 0.1;

          const time = smoothedLineTimeRef.current;
          smoothedLineTimeRef.current =
            time === 0 || Math.abs(timeTarget - time) > 10 ? timeTarget : time + (timeTarget - time) * 0.25;
        }
      }
      drawRef.current.drawChart();
      drawRef.current.drawOverlay();
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, [chartType]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={style}
      className="pointer-events-none fixed z-[9999] overflow-hidden bg-[#0C0C0C] transition-[left,top,width,height] duration-300 ease-out"
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <canvas ref={overlayCanvasRef} className="absolute inset-0 h-full w-full" />
    </motion.div>
  );
};
